// The venue-brand reading half of muay-vms src/features/website/lib/branding.ts (itself from kizzem-app
// src/lib/brand-colors.ts): the same seven colour slots and five fonts the server whitelists
// (exsportia-server controllers/venues/helpers/venue-branding.validation.js). The editing half (contrast
// checks, fix-for-me, pick-from-logo) lives in exsportia-admin Settings → Branding. MBA / MMS / VMS hold
// copies — keep the colour maths identical.
// Ported from kizzem-app src/lib/brand-colors.ts @ 3bfeffc (Justin Cohen / EAG), adapted: our 7 slots
// (textOnPrimary / textOnAccent replace highlight / textMuted), MySportia defaults instead of Kizzem's
// pink/lime, lowercase hex (the server's whitelist), CSS vars renamed to the MBA/MMS tokens, a
// contrast check + "fix for me" per text slot, a font slot, and no dependency on the DOM or zod so the
// same file runs in MMS and MBA (MBA holds a copy — keep them identical).

/**
 * A venue's branding for its public page (mysportia.com `/v/{id}`), never the admin.
 *
 * Seven colour slots and one font, all optional. Nothing set = today's MySportia look: every
 * function here returns "no override" for an empty brand, so the page's own tokens stand.
 */

export const BRAND_SLOTS = [
  'primary',
  'accent',
  'background',
  'surface',
  'text',
  'textOnPrimary',
  'textOnAccent',
] as const
export type BrandSlot = (typeof BRAND_SLOTS)[number]
export type BrandColors = Partial<Record<BrandSlot, string>>

export const BRAND_FONTS = ['hanken', 'inter', 'fraunces', 'notoSansThai', 'prompt'] as const
export type BrandFont = (typeof BRAND_FONTS)[number]

export type VenueBrand = {brandColors?: BrandColors | null; brandFont?: BrandFont | null}

/** WCAG AA for body text. A text slot under this blocks Save. */
export const MIN_CONTRAST = 4.5

/** The MySportia look, as MBA paints it today (mysportia-web `globals.css` :root: `--pink`,
 *  `--paper`, `--card`, `--ink`, white on the pink CTA). The fallback under every venue palette. */
export const DEFAULT_BRAND: Readonly<Record<BrandSlot, string>> = {
  primary: '#ff3d7e',
  accent: '#ff3d7e',
  background: '#f5f6f8',
  surface: '#ffffff',
  text: '#0e1012',
  textOnPrimary: '#ffffff',
  textOnAccent: '#ffffff',
}

const WHITE = '#ffffff'
const BLACK = '#000000'
const HEX = /^#[0-9a-fA-F]{6}$/

export function isHexColor(v: unknown): v is string {
  return typeof v === 'string' && HEX.test(v.trim())
}

/** `#ABCDEF ` → `#abcdef` — the one spelling the server accepts. */
export function normalizeHex(v: string): string {
  return v.trim().toLowerCase()
}

/** Only whitelisted slots holding a valid hex survive, lowercased. Garbage is dropped, not painted. */
export function cleanColors(colors: unknown): BrandColors {
  const out: BrandColors = {}
  if (!colors || typeof colors !== 'object') {
    return out
  }
  const src = colors as Record<string, unknown>
  for (const slot of BRAND_SLOTS) {
    const v = src[slot]
    if (isHexColor(v)) {
      out[slot] = normalizeHex(v)
    }
  }
  return out
}

export function isBrandFont(v: unknown): v is BrandFont {
  return typeof v === 'string' && (BRAND_FONTS as readonly string[]).includes(v)
}

/** The schema: whatever the venue doc holds, read back as a clean brand. */
export function parseBrand(raw: unknown): VenueBrand {
  const src = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {}
  return {
    brandColors: cleanColors(src.brandColors),
    brandFont: isBrandFont(src.brandFont) ? src.brandFont : null,
  }
}

export function hasBranding(brand: VenueBrand | null | undefined): boolean {
  return Object.keys(cleanColors(brand?.brandColors)).length > 0 || isBrandFont(brand?.brandFont)
}

/** The PUT /venues/:id/branding body: the whole palette (an empty one clears) and the font. */
export function buildBrandingBody(brand: VenueBrand): {
  brandColors: BrandColors | null
  brandFont: BrandFont | null
} {
  const colors = cleanColors(brand.brandColors)
  return {
    brandColors: Object.keys(colors).length ? colors : null,
    brandFont: isBrandFont(brand.brandFont) ? brand.brandFont : null,
  }
}

export function sameBrand(a: VenueBrand, b: VenueBrand): boolean {
  return JSON.stringify(buildBrandingBody(a)) === JSON.stringify(buildBrandingBody(b))
}

/* ---------- colour maths (WCAG) ---------- */

function channels(hex: string): [number, number, number] {
  const n = parseInt(hex.slice(1), 16)
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255]
}

function toHex([r, g, b]: [number, number, number]): string {
  return `#${[r, g, b].map((c) => Math.round(c).toString(16).padStart(2, '0')).join('')}`
}

/** WCAG relative luminance, 0 (black) to 1 (white). */
export function luminance(hex: string): number {
  const chan = (c: number) => {
    const s = c / 255
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4)
  }
  const [r, g, b] = channels(hex)
  return 0.2126 * chan(r) + 0.7152 * chan(g) + 0.0722 * chan(b)
}

/** WCAG contrast between two colours, 1 (identical) to 21 (black on white). */
export function contrastRatio(a: string, b: string): number {
  const [l1, l2] = [luminance(a), luminance(b)].sort((x, y) => y - x)
  return (l1 + 0.05) / (l2 + 0.05)
}

/** `hex` carried `amount` (0–1) of the way toward `target`. */
export function mix(hex: string, target: string, amount: number): string {
  const a = channels(hex)
  const b = channels(target)
  return toHex([0, 1, 2].map((i) => a[i] + (b[i] - a[i]) * amount) as [number, number, number])
}

/** The text colour that reads best on a fill: the page ink or white, whichever contrasts more. */
export function readableOn(fill: string, ink = DEFAULT_BRAND.text): string {
  return contrastRatio(ink, fill) >= contrastRatio(WHITE, fill) ? ink : WHITE
}

/** A fill colour made safe to use as TYPE on `ground`: darkened until it reads at 4.5:1. A colour
 *  that already reads comes back untouched. */
export function readableCompanion(hex: string, ground = WHITE): string {
  let out = normalizeHex(hex)
  for (let i = 0; i < 24 && contrastRatio(out, ground) < MIN_CONTRAST; i++) {
    out = mix(out, BLACK, 0.12)
  }
  return out
}

/* ---------- the resolved palette ---------- */

/**
 * The venue's palette with MySportia filled in. The text on a fill the venue did not choose is
 * computed from the fill (`readableOn`), so setting a yellow primary never leaves white type on it.
 * An unset accent follows the primary — that is how the page works today (one colour for both).
 */
export function resolveBrand(colors: BrandColors | null | undefined): Record<BrandSlot, string> {
  const c = cleanColors(colors)
  const primary = c.primary ?? DEFAULT_BRAND.primary
  const accent = c.accent ?? (c.primary ? primary : DEFAULT_BRAND.accent)
  return {
    primary,
    accent,
    background: c.background ?? DEFAULT_BRAND.background,
    surface: c.surface ?? DEFAULT_BRAND.surface,
    text: c.text ?? DEFAULT_BRAND.text,
    textOnPrimary: c.textOnPrimary ?? (c.primary ? readableOn(primary) : DEFAULT_BRAND.textOnPrimary),
    textOnAccent: c.textOnAccent ?? (c.accent || c.primary ? readableOn(accent) : DEFAULT_BRAND.textOnAccent),
  }
}

/* ---------- fonts ---------- */

/** Five hosted Google faces. Thai and Cyrillic text falls through the stack to faces that carry
 *  it (Prompt / Noto Sans Thai carry Thai; Inter / Hanken carry Cyrillic). */
export const BRAND_FONT_FACES: Readonly<Record<BrandFont, {family: string; weights: string}>> = {
  hanken: {family: 'Hanken Grotesk', weights: '400;500;600;700;800'},
  inter: {family: 'Inter', weights: '400;500;600;700;800'},
  fraunces: {family: 'Fraunces', weights: '400;600;700;800'},
  notoSansThai: {family: 'Noto Sans Thai', weights: '400;500;600;700'},
  prompt: {family: 'Prompt', weights: '400;500;600;700;800'},
}

// eslint-disable-next-line quotes
export const BRAND_FONT_FALLBACK = "'Noto Sans Thai', 'Noto Sans Myanmar', system-ui, sans-serif"

export function brandFontFamily(font: BrandFont, fallback = BRAND_FONT_FALLBACK): string {
  return `'${BRAND_FONT_FACES[font].family}', ${fallback}`
}

/** The Google Fonts stylesheet for one face — loaded only where the brand is painted. */
export function brandFontCssUrl(font: BrandFont): string {
  const {family, weights} = BRAND_FONT_FACES[font]
  return `https://fonts.googleapis.com/css2?family=${family.replace(/ /g, '+')}:wght@${weights}&display=swap`
}
